import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useAssetStalenessQuery } from '@verantos-dev/data-access-cohorts';

import type { SourceCohortDisplay, UseSourceCohortSectionResult } from '../types';
import { useAnalysisEditLock } from './useAnalysisEditLock';
import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { useAnalysisSourceCohort } from './useAnalysisSourceCohort';
import { getSubjectCountLabel } from '../utils/subjectCountLabel';

// Gates the stale warning next to the source cohort, with the rest of the analysis.
// TODO: cleanup with descriptive_analysis_impl ff
const STALE_COHORT_ICON_FLAG = 'descriptive_analysis_impl';

// `parentLoading` is the section's own `isLoading` prop (the definition tab
// still hydrating) — combined here with the source-cohort fetch's loading
// state so the container only needs a single `isLoading` flag to render.
export const useSourceCohortSection = (parentLoading = false): UseSourceCohortSectionResult => {
  const { formatMessage } = useIntl();
  const { projectId } = useAnalysisRouteParams();
  const { selectedCohort, isLoading: isSectionLoading } = useAnalysisSourceCohort();
  const { isEditDisabled, isProcessing } = useAnalysisEditLock();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // The same cohort stale check the cohort page and the analytical dataset page read. It is keyed on the cohort
  // *version* id, which the project cohort list already carries, and stays idle while that id is empty.
  const isStaleIconEnabled = Boolean(useFeatureFlagSplit(STALE_COHORT_ICON_FLAG));
  const cohortVersionId = selectedCohort?.latestCohortVersionId ?? '';
  const { data: staleness } = useAssetStalenessQuery({
    kind: 'cohort',
    id: cohortVersionId,
    enabled: isStaleIconEnabled && Boolean(cohortVersionId),
  });
  const isStale = isStaleIconEnabled && (staleness?.isStale ?? false);

  const cohort = useMemo<SourceCohortDisplay | null>(() => {
    if (!selectedCohort) return null;
    return {
      cohortProfileId: selectedCohort.cohortProfileId,
      cohortName: selectedCohort.cohortName,
      status: selectedCohort.latestStatus,
      subjectCount: selectedCohort.latestSubjectCount,
      isStale,
    };
  }, [selectedCohort, isStale]);

  // Not-yet-PROCESSED cohorts have no real subject count (null) — show a
  // placeholder rather than hiding the caption.
  const metaLine = useMemo(() => {
    if (!cohort) return '';
    return getSubjectCountLabel(cohort.subjectCount);
  }, [cohort]);

  const onAddCohort = useCallback(() => setIsPickerOpen(true), []);
  const onClosePicker = useCallback(() => setIsPickerOpen(false), []);

  const editLockTooltip = isEditDisabled
    ? formatMessage({
        id: isProcessing
          ? 'analysisDetail.editLock.processing'
          : 'analysisDetail.editLock.notOwner',
      })
    : undefined;

  return {
    projectId,
    sectionLabel: formatMessage({ id: 'analysisDetail.sourceCohort.label' }),
    // Always "Add cohort" — no "Replace cohort" variant, even with one attached.
    addLabel: formatMessage({ id: 'analysisDetail.sourceCohort.addButton' }),
    switchLabel: formatMessage({ id: 'analysisDetail.sourceCohort.switchButton' }),
    notSelectedText: formatMessage({ id: 'analysisDetail.sourceCohort.notSelected' }),
    cohort,
    metaLine,
    staleCohortTooltip: formatMessage({ id: 'analysisDetail.sourceCohort.staleWarning' }),
    isLoading: parentLoading || isSectionLoading,
    isPickerOpen,
    onAddCohort,
    onClosePicker,
    isEditDisabled,
    editLockTooltip,
  };
};
